import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { adminUpdateHomeContent } from '@/lib/api';
import { Plus, Trash2, Save, Image } from 'lucide-react';

export default function HomeContentEditor({ initialContent, onSave }) {
  const [content, setContent] = useState(initialContent);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminUpdateHomeContent(content);
      toast.success('Home page content saved!');
      if (onSave) onSave(content);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  const updateService = (index, field, value) => {
    const services = [...content.services];
    services[index] = { ...services[index], [field]: value };
    setContent(prev => ({ ...prev, services }));
  };

  const addService = () => {
    setContent(prev => ({
      ...prev,
      services: [...(prev.services || []), { title: '', description: '', icon: 'wrench' }]
    }));
  };

  const removeService = (index) => {
    setContent(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }));
  };

  const addGalleryImage = () => {
    setContent(prev => ({
      ...prev,
      gallery_images: [...(prev.gallery_images || []), { url: '', caption: '' }]
    }));
  };

  const updateGalleryImage = (index, field, value) => {
    const images = [...(content.gallery_images || [])];
    images[index] = { ...images[index], [field]: value };
    setContent(prev => ({ ...prev, gallery_images: images }));
  };

  const removeGalleryImage = (index) => {
    setContent(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((_, i) => i !== index)
    }));
  };

  const addTikTokVideo = () => {
    setContent(prev => ({
      ...prev,
      tiktok_video_urls: [...(prev.tiktok_video_urls || []), '']
    }));
  };

  const updateTikTokVideo = (index, value) => {
    const urls = [...(content.tiktok_video_urls || [])];
    urls[index] = value;
    setContent(prev => ({ ...prev, tiktok_video_urls: urls }));
  };

  const removeTikTokVideo = (index) => {
    setContent(prev => ({
      ...prev,
      tiktok_video_urls: (prev.tiktok_video_urls || []).filter((_, i) => i !== index)
    }));
  };

  const services = content.services || [];
  const galleryImages = content.gallery_images || [];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wide text-white">
          Edit Home Page
        </h2>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-green-600 hover:bg-green-700"
          data-testid="save-home-btn"
        >
          {saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" />Save Changes</>}
        </Button>
      </div>

      {/* Hero Section */}
      <div className="glass p-6 border border-zinc-800 bg-zinc-900/50 space-y-4">
        <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-white">Hero Section</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-zinc-300">Title</Label>
            <Input
              value={content.hero_title || ''}
              onChange={(e) => updateField('hero_title', e.target.value)}
              className="bg-zinc-950 border-zinc-800"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">Subtitle</Label>
            <Input
              value={content.hero_subtitle || ''}
              onChange={(e) => updateField('hero_subtitle', e.target.value)}
              className="bg-zinc-950 border-zinc-800"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-300">Description</Label>
          <Textarea
            value={content.hero_description || ''}
            onChange={(e) => updateField('hero_description', e.target.value)}
            className="bg-zinc-950 border-zinc-800 min-h-[80px]"
          />
        </div>
      </div>

      {/* About Section */}
      <div className="glass p-6 border border-zinc-800 bg-zinc-900/50 space-y-4">
        <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-white">About Section</h3>
        <div className="space-y-2">
          <Label className="text-zinc-300">Title</Label>
          <Input
            value={content.about_title || ''}
            onChange={(e) => updateField('about_title', e.target.value)}
            className="bg-zinc-950 border-zinc-800"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-300">Description</Label>
          <Textarea
            value={content.about_description || ''}
            onChange={(e) => updateField('about_description', e.target.value)}
            className="bg-zinc-950 border-zinc-800 min-h-[100px]"
          />
        </div>
      </div>

      {/* Services Section */}
      <div className="glass p-6 border border-zinc-800 bg-zinc-900/50 space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-white">Services</h3>
          <Button variant="outline" size="sm" onClick={addService} className="border-zinc-700">
            <Plus className="w-4 h-4 mr-2" />Add Service
          </Button>
        </div>
        <div className="space-y-4">
          {services.map((service, index) => (
            <ServiceEditor 
              key={index} 
              service={service} 
              index={index}
              onUpdate={updateService}
              onRemove={removeService}
            />
          ))}
        </div>
      </div>

      {/* Gallery Section */}
      <div className="glass p-6 border border-zinc-800 bg-zinc-900/50 space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-white">
            <Image className="w-5 h-5 inline mr-2" />Gallery Images
          </h3>
          <Button variant="outline" size="sm" onClick={addGalleryImage} className="border-zinc-700">
            <Plus className="w-4 h-4 mr-2" />Add Image
          </Button>
        </div>
        <p className="text-zinc-500 text-sm">Add image URLs to display in your gallery</p>
        <div className="space-y-4">
          {galleryImages.map((image, index) => (
            <GalleryEditor 
              key={index} 
              image={image} 
              index={index}
              onUpdate={updateGalleryImage}
              onRemove={removeGalleryImage}
            />
          ))}
          {galleryImages.length === 0 && (
            <p className="text-zinc-600 text-center py-4">No gallery images yet</p>
          )}
        </div>
      </div>

      {/* Contact Section */}
      <div className="glass p-6 border border-zinc-800 bg-zinc-900/50 space-y-4">
        <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-white">Contact Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-zinc-300">Contact Name</Label>
            <Input
              value={content.contact_name || ''}
              onChange={(e) => updateField('contact_name', e.target.value)}
              className="bg-zinc-950 border-zinc-800"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">Phone</Label>
            <Input
              value={content.contact_phone || ''}
              onChange={(e) => updateField('contact_phone', e.target.value)}
              className="bg-zinc-950 border-zinc-800"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">Email</Label>
            <Input
              value={content.contact_email || ''}
              onChange={(e) => updateField('contact_email', e.target.value)}
              className="bg-zinc-950 border-zinc-800"
            />
          </div>
        </div>
      </div>

      {/* Meta Integration Note */}
      <div className="glass p-6 border border-yellow-800/50 bg-yellow-900/10 space-y-2">
        <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-yellow-500">Meta Integration (Coming Soon)</h3>
        <p className="text-zinc-400 text-sm">
          Facebook reviews and Instagram feed will appear on the home page once Meta Business Suite integration is configured.
        </p>
      </div>

      {/* TikTok Section */}
      <div className="glass p-6 border border-zinc-800 bg-zinc-900/50 space-y-4">
        <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-white">TikTok</h3>
        <div className="space-y-2">
          <Label className="text-zinc-300">TikTok Profile URL</Label>
          <Input
            value={content.tiktok_profile_url || ''}
            onChange={(e) => updateField('tiktok_profile_url', e.target.value)}
            className="bg-zinc-950 border-zinc-800"
            placeholder="https://www.tiktok.com/@yourhandle"
            data-testid="tiktok-profile-url-input"
          />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-zinc-300">Featured Video URLs</Label>
            <Button
              variant="outline"
              size="sm"
              onClick={addTikTokVideo}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              data-testid="add-tiktok-video-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Video
            </Button>
          </div>
          <p className="text-zinc-500 text-xs">
            Paste the full URL of a TikTok video (e.g. https://www.tiktok.com/@nakituneperformance/video/1234567890...)
          </p>
          {(content.tiktok_video_urls || []).map((url, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={url}
                onChange={(e) => updateTikTokVideo(index, e.target.value)}
                className="bg-zinc-950 border-zinc-800"
                placeholder="https://www.tiktok.com/@user/video/..."
                data-testid={`tiktok-video-url-${index}`}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeTikTokVideo(index)}
                className="text-red-400 hover:text-red-300"
                data-testid={`remove-tiktok-video-${index}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          {(content.tiktok_video_urls || []).length === 0 && (
            <p className="text-zinc-600 text-center py-4 text-sm">No TikTok videos added yet</p>
          )}
        </div>
      </div>
    </div>
  );
}

function ServiceEditor({ service, index, onUpdate, onRemove }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 bg-zinc-950 border border-zinc-800">
      <div className="md:col-span-4 space-y-2">
        <Label className="text-zinc-400 text-xs">Title</Label>
        <Input
          value={service.title || ''}
          onChange={(e) => onUpdate(index, 'title', e.target.value)}
          className="bg-zinc-900 border-zinc-800"
          placeholder="Service title"
        />
      </div>
      <div className="md:col-span-5 space-y-2">
        <Label className="text-zinc-400 text-xs">Description</Label>
        <Input
          value={service.description || ''}
          onChange={(e) => onUpdate(index, 'description', e.target.value)}
          className="bg-zinc-900 border-zinc-800"
          placeholder="Service description"
        />
      </div>
      <div className="md:col-span-2 space-y-2">
        <Label className="text-zinc-400 text-xs">Icon</Label>
        <Select value={service.icon || 'wrench'} onValueChange={(v) => onUpdate(index, 'icon', v)}>
          <SelectTrigger className="bg-zinc-900 border-zinc-800">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="gauge">Gauge</SelectItem>
            <SelectItem value="zap">Zap</SelectItem>
            <SelectItem value="wrench">Wrench</SelectItem>
            <SelectItem value="award">Award</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="md:col-span-1 flex items-end">
        <Button variant="ghost" size="sm" onClick={() => onRemove(index)} className="text-red-400 hover:text-red-300">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function GalleryEditor({ image, index, onUpdate, onRemove }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 bg-zinc-950 border border-zinc-800">
      <div className="md:col-span-7 space-y-2">
        <Label className="text-zinc-400 text-xs">Image URL</Label>
        <Input
          value={image.url || ''}
          onChange={(e) => onUpdate(index, 'url', e.target.value)}
          className="bg-zinc-900 border-zinc-800"
          placeholder="https://..."
        />
      </div>
      <div className="md:col-span-4 space-y-2">
        <Label className="text-zinc-400 text-xs">Caption (optional)</Label>
        <Input
          value={image.caption || ''}
          onChange={(e) => onUpdate(index, 'caption', e.target.value)}
          className="bg-zinc-900 border-zinc-800"
          placeholder="Image caption"
        />
      </div>
      <div className="md:col-span-1 flex items-end">
        <Button variant="ghost" size="sm" onClick={() => onRemove(index)} className="text-red-400 hover:text-red-300">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
